import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { MemberSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import {
  MessageSquare, Plus, Loader2, AlertTriangle, ShieldAlert, Send, X,
  Clock, CheckCircle, XCircle, Eye, Search
} from 'lucide-react';
import { AttachmentUploader, AttachmentGallery } from './AttachmentUploader';
import { ImageWithFallback } from './ui/image-with-fallback';

const STATUS_STYLE = {
  open:       { label: 'Open',       bg: 'bg-amber-500/10',  text: 'text-amber-400',  icon: Clock },
  in_review:  { label: 'In Review',  bg: 'bg-[#00E5FF]/10',  text: 'text-[#00E5FF]',  icon: Eye },
  resolved:   { label: 'Resolved',   bg: 'bg-[#00E676]/10',  text: 'text-[#00E676]',  icon: CheckCircle },
  rejected:   { label: 'Rejected',   bg: 'bg-[#FF1744]/10',  text: 'text-[#FF1744]',  icon: XCircle },
  closed:     { label: 'Closed',     bg: 'bg-white/5',       text: 'text-[#5C7599]',  icon: X },
};

const TYPE_LABEL = {
  merchant_report: 'Merchant Report',
  merchant_problem: 'Merchant Problem',
  member_issue: 'Member Issue',
  other: 'Other',
};

export function SupportPage() {
  const { user, api } = useAuth();
  const [searchParams] = useSearchParams();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [reasons, setReasons] = useState({ member_reasons: [], merchant_reasons: [] });
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [posting, setPosting] = useState(false);
  const [search, setSearch] = useState('');

  const isMerchant = user?.role === 'merchant';

  const emptyForm = {
    type: isMerchant ? 'merchant_problem' : 'merchant_report',
    subject: '',
    description: '',
    reason_code: '',
    reported_merchant_id: searchParams.get('merchant') || '',
  };
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [evidenceUrls, setEvidenceUrls] = useState([]);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  const fetchTickets = useCallback(async () => {
    try {
      const { data } = await api.get('/api/support/tickets/mine?limit=50');
      setTickets(data.tickets || []);
    } catch (e) {
      toast.error('Failed to load tickets');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    fetchTickets();
    api.get('/api/support/reasons').then(({ data }) => setReasons(data)).catch(() => {});
  }, [fetchTickets, api]);

  // If ?merchant=ID is passed, open the create dialog automatically
  useEffect(() => {
    const mid = searchParams.get('merchant');
    if (!mid) return;
    setForm(f => ({ ...f, reported_merchant_id: mid, type: 'merchant_report' }));
    setCreateOpen(true);
  }, [searchParams]);

  const handleCreate = async () => {
    if (!form.subject.trim()) { toast.error('Subject is required'); return; }
    if (form.description.trim().length < 10) { toast.error('Please provide at least 10 chars'); return; }
    setSubmitting(true);
    try {
      await api.post('/api/support/tickets', { ...form, evidence_urls: evidenceUrls });
      toast.success('Your ticket has been submitted. Support will get back to you.');
      setCreateOpen(false);
      setForm(emptyForm);
      setEvidenceUrls([]);
      fetchTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    }
    setSubmitting(false);
  };

  const openDetail = async (t) => {
    setSelected(t);
    try {
      const { data } = await api.get(`/api/support/tickets/${t.id}`);
      setSelected(data);
    } catch (e) {/* keep stale */}
  };

  const handleReply = async () => {
    if (!reply.trim() && replyAttachments.length === 0) return;
    setPosting(true);
    try {
      await api.post(`/api/support/tickets/${selected.id}/messages`, {
        content: reply || '(attached evidence)',
        attachments: replyAttachments,
      });
      const { data } = await api.get(`/api/support/tickets/${selected.id}`);
      setSelected(data);
      setReply('');
      setReplyAttachments([]);
      fetchTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reply');
    }
    setPosting(false);
  };

  const closeTicket = async () => {
    try {
      await api.post(`/api/support/tickets/${selected.id}/close`);
      toast.success('Ticket closed');
      setSelected(null);
      fetchTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to close');
    }
  };

  const list = tickets.filter(t => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (t.subject || '').toLowerCase().includes(s) ||
      (t.reported_merchant_name || '').toLowerCase().includes(s);
  });

  const reasonOptions = isMerchant ? reasons.merchant_reasons : reasons.member_reasons;

  return (
    <MemberSidebarLayout>
      <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="support-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-outfit text-2xl font-bold text-white flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-[#00E5FF]" /> Support & Disputes
            </h1>
            <p className="text-[#A0B4CC] text-sm mt-1">
              {isMerchant
                ? 'Report problems you’re facing or escalate issues to our team.'
                : 'Report a merchant or raise any issue with the AGA team.'}
            </p>
          </div>
          <Button onClick={() => { setForm(emptyForm); setCreateOpen(true); }}
            className="bg-[#00E676] text-[#000B18] hover:bg-[#00E676]/90"
            data-testid="new-ticket-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Ticket
          </Button>
        </div>

        <div className="relative mb-6 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C7599]" />
          <Input
            placeholder="Search my tickets..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 bg-[#041A3B] border-white/10 text-white"
            data-testid="ticket-search"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
          </div>
        ) : list.length === 0 ? (
          <Card className="bg-[#041A3B] border-white/10">
            <CardContent className="p-10 text-center">
              <ShieldAlert className="w-10 h-10 mx-auto mb-3 text-[#5C7599]" />
              <p className="text-[#A0B4CC] text-sm">No tickets yet. Click <span className="text-white font-medium">New Ticket</span> to start one.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {list.map(t => {
              const st = STATUS_STYLE[t.status] || STATUS_STYLE.open;
              const StIcon = st.icon;
              return (
                <Card key={t.id} className="bg-[#041A3B] border-white/10 hover:border-white/20 transition-colors cursor-pointer"
                  onClick={() => openDetail(t)} data-testid={`ticket-card-${t.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-white font-semibold text-sm">{t.subject}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${st.bg} ${st.text} inline-flex items-center gap-1`}>
                            <StIcon className="w-3 h-3" /> {st.label}
                          </span>
                          <span className="px-2 py-0.5 rounded-full bg-white/5 text-[#A0B4CC] text-[10px]">
                            {TYPE_LABEL[t.type] || t.type}
                          </span>
                        </div>
                        {t.reported_merchant_name && (
                          <p className="text-[#5C7599] text-xs mt-1">Against: <span className="text-white">{t.reported_merchant_name}</span></p>
                        )}
                        <p className="text-[#5C7599] text-[11px] mt-1">
                          Updated {new Date(t.updated_at).toLocaleString()} · {(t.messages || []).length} message{(t.messages||[]).length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create Ticket Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="bg-[#041A3B] border-white/10 max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                {isMerchant ? 'Report a Problem' : 'New Support Ticket'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-[#A0B4CC] text-xs">Type *</Label>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value, reason_code: '' })}
                  className="w-full bg-[#000B18] border border-white/20 text-white rounded-md px-3 py-2 text-sm"
                  data-testid="ticket-type">
                  {!isMerchant && <option value="merchant_report">Report a merchant</option>}
                  {!isMerchant && <option value="member_issue">General issue</option>}
                  {isMerchant && <option value="merchant_problem">Merchant problem</option>}
                  <option value="other">Other</option>
                </select>
              </div>

              {(form.type === 'merchant_report' || form.type === 'merchant_problem') && (
                <div>
                  <Label className="text-[#A0B4CC] text-xs">Reason *</Label>
                  <select value={form.reason_code} onChange={e => setForm({ ...form, reason_code: e.target.value })}
                    className="w-full bg-[#000B18] border border-white/20 text-white rounded-md px-3 py-2 text-sm"
                    data-testid="ticket-reason">
                    <option value="">Select a reason…</option>
                    {(form.type === 'merchant_report' ? reasons.member_reasons : reasons.merchant_reasons).map(r => (
                      <option key={r.code} value={r.code}>{r.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {form.type === 'merchant_report' && !form.reported_merchant_id && (
                <div className="p-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-xs text-amber-300">
                  Tip: open a merchant&apos;s page in the Marketplace and click <span className="font-medium">Report this merchant</span> to auto-fill this.
                </div>
              )}
              {form.reported_merchant_id && (
                <div className="text-[10px] text-[#5C7599]">Reporting merchant ID: {form.reported_merchant_id}</div>
              )}

              <div>
                <Label className="text-[#A0B4CC] text-xs">Subject *</Label>
                <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
                  placeholder="A short headline"
                  className="bg-[#000B18] border-white/20 text-white"
                  data-testid="ticket-subject" />
              </div>
              <div>
                <Label className="text-[#A0B4CC] text-xs">What happened? *</Label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={5}
                  placeholder="Describe the issue. Include dates, amounts, and any context our team should know."
                  className="w-full px-3 py-2 rounded-md bg-[#000B18] border border-white/20 text-white text-sm focus:border-[#00E5FF] focus:outline-none resize-none"
                  data-testid="ticket-description" />
                <p className="text-[10px] text-[#5C7599] mt-1">Minimum 10 characters · Our team typically responds within 1 business day.</p>
              </div>
              <div>
                <Label className="text-[#A0B4CC] text-xs">Evidence (optional)</Label>
                <p className="text-[10px] text-[#5C7599] mb-1.5">Screenshots, receipts, or photos help our team investigate faster.</p>
                <AttachmentUploader api={api} value={evidenceUrls} onChange={setEvidenceUrls} testIdPrefix="ticket-evidence" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setCreateOpen(false)} className="text-[#A0B4CC]">Cancel</Button>
              <Button onClick={handleCreate} disabled={submitting}
                className="bg-[#00E676] text-[#000B18] hover:bg-[#00E676]/90"
                data-testid="submit-ticket-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Submit</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ticket Detail */}
        <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <DialogContent className="bg-[#041A3B] border-white/10 max-w-xl max-h-[85vh] overflow-y-auto">
            {selected && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-white flex items-start gap-2">
                    <MessageSquare className="w-5 h-5 text-[#00E5FF] mt-0.5" />
                    <div>
                      <span>{selected.subject}</span>
                      <div className="text-[10px] text-[#5C7599] font-normal mt-0.5">
                        {TYPE_LABEL[selected.type]} · #{selected.id?.slice(0, 8)}
                      </div>
                    </div>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    {(() => {
                      const st = STATUS_STYLE[selected.status] || STATUS_STYLE.open;
                      const StIcon = st.icon;
                      return (
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${st.bg} ${st.text} inline-flex items-center gap-1`}>
                          <StIcon className="w-3 h-3" /> {st.label}
                        </span>
                      );
                    })()}
                    {selected.reason_code && (
                      <span className="text-[10px] text-[#5C7599]">Reason: {selected.reason_code.replace(/_/g, ' ')}</span>
                    )}
                  </div>

                  {selected.reported_merchant_name && (
                    <div className="p-2 rounded-lg bg-[#000B18] text-xs text-[#A0B4CC]">
                      Against: <span className="text-white font-medium">{selected.reported_merchant_name}</span>
                    </div>
                  )}

                  {/* Thread */}
                  <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1" data-testid="ticket-thread">
                    {(selected.messages || []).map(m => {
                      const isStaff = m.is_staff || ['admin', 'cro', 'moderator', 'mro'].includes(m.sender_role);
                      const isMe = m.sender_id === user?.id;
                      return (
                        <div key={m.id} className={`p-3 rounded-lg ${isStaff ? 'bg-[#00E5FF]/5 border border-[#00E5FF]/20' : 'bg-[#000B18] border border-white/5'}`}>
                          <div className="flex items-center justify-between text-[10px] text-[#5C7599] mb-1">
                            <span className={isStaff ? 'text-[#00E5FF] font-medium' : 'text-white'}>
                              {isMe ? 'You' : m.sender_name || m.sender_role}
                              {isStaff && <span className="ml-1 px-1.5 py-0.5 rounded bg-[#00E5FF]/10">Staff</span>}
                            </span>
                            <span>{new Date(m.created_at).toLocaleString()}</span>
                          </div>
                          <p className="text-[#A0B4CC] text-sm whitespace-pre-wrap">{m.content}</p>
                          <AttachmentGallery attachments={m.attachments} onOpen={(url) => setLightbox(url)} />
                        </div>
                      );
                    })}
                  </div>

                  {/* Reply */}
                  {selected.status !== 'closed' && (
                    <div className="space-y-2">
                      <textarea value={reply} onChange={e => setReply(e.target.value)}
                        rows={3}
                        placeholder="Add a reply…"
                        className="w-full px-3 py-2 rounded-md bg-[#000B18] border border-white/20 text-white text-sm focus:border-[#00E5FF] focus:outline-none resize-none"
                        data-testid="ticket-reply-input" />
                      <AttachmentUploader api={api} value={replyAttachments} onChange={setReplyAttachments} testIdPrefix="reply-evidence" />
                      <div className="flex items-center justify-end gap-2 mt-2">
                        {selected.reporter_id === user?.id && (
                          <Button variant="ghost" onClick={closeTicket} className="text-[#A0B4CC]" data-testid="close-ticket-btn">
                            <X className="w-4 h-4 mr-1.5" /> Close ticket
                          </Button>
                        )}
                        <Button onClick={handleReply} disabled={posting || (!reply.trim() && replyAttachments.length === 0)}
                          className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90"
                          data-testid="post-reply-btn">
                          {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Reply</>}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Lightbox for viewing attachments full-screen */}
        {lightbox && (
          <div
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            data-testid="attachment-lightbox"
          >
            <ImageWithFallback
              src={lightbox}
              alt="Evidence"
              className="max-w-full max-h-full object-contain rounded-lg"
              fallback={
                <div className="bg-[#041A3B] border border-white/10 rounded-lg p-8 text-center text-[#A0B4CC]">
                  <p className="text-sm">This attachment cannot be displayed.</p>
                  <p className="text-xs text-[#5C7599] mt-1">The image link may have expired.</p>
                </div>
              }
            />
            <button
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
              onClick={() => setLightbox(null)}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </MemberSidebarLayout>
  );
}
